import { Reducer, useEffect, useReducer } from 'react';
import { rm } from 'utils';
import { Button } from 'components/Button';
import { LoadingStatus } from 'models/LoadingStatus';
import { PolicyInformation } from 'models/PolicyInformation';
import { getSalePolicy } from 'services/ApiCheckoutPageService';
import { Spinner } from 'components/Shared/Loader';
import Markdown from 'components/Shared/Markdown';
import styles from './salesPolicyModal.scss';

interface Props {
  onClose: () => void;
}

interface OwnState {
  loading: LoadingStatus;
  policies: PolicyInformation[];
  selected: PolicyInformation | undefined;
}

type Action =
  | { type: 'loadingDone'; policies: PolicyInformation[] }
  | { type: 'loadingError' }
  | { type: 'select'; selected: PolicyInformation | undefined };

const initialState: OwnState = {
  loading: LoadingStatus.Loading,
  policies: [],
  selected: undefined
};

const ownReducer: Reducer<OwnState, Action> = (state, action) => {
  switch (action.type) {
    case 'loadingDone':
      return { ...state, loading: LoadingStatus.Completed, policies: action.policies };
    case 'loadingError':
      return { ...state, loading: LoadingStatus.Error, policies: [] };
    case 'select':
      return { ...state, selected: action.selected };
    default:
      return state;
  }
};

const SalesPolicyModal = ({ onClose }: Props) => {
  const [state, dispatch] = useReducer(ownReducer, initialState);

  useEffect(() => {
    getSalePolicy()
      .then(policies => {
        dispatch({ type: 'loadingDone', policies });
      })
      .catch(() => {
        dispatch({ type: 'loadingError' });
      });
  }, []);

  const selectPolicy = (p: PolicyInformation) => {
    dispatch({ type: 'select', selected: p });
  };

  const deselectPolicy = () => {
    dispatch({ type: 'select', selected: undefined });
  };

  return (
    <Spinner loading={state.loading === LoadingStatus.Loading} className={styles.spinner}>
      <h2 className={styles.policyTitle}>{rm('checkout_our_terms')}</h2>
      {state.selected ? (
        <div className={styles.content}>
          <Markdown {...state.selected.component} />
        </div>
      ) : (
        <div className={styles.policyLinksWrapper}>
          <ul className={styles.policyLinks}>
            {state.policies.map(p => (
              <li key={p.title}>
                <Button appearance="link">
                  <h4 onClick={() => selectPolicy(p)}>{p.title}</h4>
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className={styles.footerButtons}>
        {state.selected && (
          <Button className={styles.buttonBack} onClick={deselectPolicy}>
            {rm('navigation_backbutton')}
          </Button>
        )}
        <Button className={styles.buttonClose} onClick={onClose}>
          {rm('shared_button_close')}
        </Button>
      </div>
    </Spinner>
  );
};

export default SalesPolicyModal;
